import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnDestroy,
  inject,
} from "@angular/core";
import { toolbarOptions } from "../../../shared/model/toolbar-constants";
import { forkJoin, Subscription } from "rxjs";
import { ToolbarService } from "../../services/internal-services/toolbar.service";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { MatSelectChange } from "@angular/material/select";
import { IProjectAOI } from "../../interfaces/i-project-aoi";
import { GeoService } from "../../services/internal-services/geo.service";
import { FormControl, FormGroup } from "@angular/forms";
import { ApiViewerService } from "../../services/internal-services/api-viewer.service";
import { MapStatus } from "../../model/map-status";
import { saveAs } from "file-saver";

import SquareSelectionTool from "@vision/core-map-api/src/measures/tools/internal/SquareSelectionTool";
import PolygonSelectionTool from "@vision/core-map-api/src/measures/tools/internal/PolygonSelectionTool";
import CircleSelectionTool from "@vision/core-map-api/src/measures/tools/internal/CircleSelectionTool";
import { Tools } from "@vision/core-map-api/src/constants/CoreMapConstants";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../shared/model/google-analytics-constants";
import { MeasurementCommunicatorService } from "../../../toolbars-components/services/measurement-communicator.service";
import { MatDialog } from "@angular/material/dialog";
import { UploadSelectionFileDialogComponent } from "../../../dialog-components/upload-selection-file-dialog/upload-selection-file-dialog.component";
import { MultipleCollectionsWarningComponent } from "../../../dialog-components/multiple-collections-warning/multiple-collections-warning.component";
import JSZip from "jszip";
import { PhoenixMultiExportGeotiffDialog } from "../../../dialog-components/multi-export-geotiff-dialog/phoenix-multi-export-geotiff-dialog";
import { MapService } from "../../services/internal-services/map.service";
import { FeatureFlagsService } from "../../services/internal-services/feature-flags.service";
import { FeatureFlags } from "../../model/feature-flags";
import { GoogleAnalyticsService } from "../../../shared/services/google-analytics.service";
import { VIEWER_SERVICE } from "../../services/internal-services/viewer-services/inject-base-viewer.service";
import GeoJSON from "ol/format/GeoJSON";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-export-geotiff",
  templateUrl: "./export-geotiff.component.html",
  styleUrls: ["./export-geotiff.component.scss"],
  standalone: false,
})
export class ExportGeotiffComponent implements OnDestroy, AfterViewInit {
  viewerService = inject(VIEWER_SERVICE);

  TILES_LIMIT = 1024;

  MULTI_EXPORT_TILES_LIMIT = 12288;

  toolbarOption = toolbarOptions.EXPORT_GEOTIFF;

  toolsIds = ViewerConstants.EXTERNAL_TOOLS;

  private _exportGeoTiffEnabled = false;

  private _centerAOI: IProjectAOI;

  private _mapStatus: MapStatus;

  private _mode: any;

  private _zoom: number;

  showDimensions = false;

  displayPanel = false;

  downloadingGeoTiff = false;

  tilesLimitExceeded = false;

  multiExportTilesLimitExceeded = false;

  selectedTool = null;

  selectionGeometry = null;

  warningMessage = null;

  errorMessage = null;

  tilesSelected = null;

  editActionEnabled = false;

  multipleCollections = false;

  swipeEnabled: boolean = false;

  tools = [];

  subscriptions: Subscription[] = [];

  //Initial values
  selectedZoom = 21;

  selectedClipMode = "exact";

  extractValues = [];

  defaultZoomLevels = [
    { value: 16, desc: "16" },
    { value: 17, desc: "17" },
    { value: 18, desc: "18" },
    { value: 19, desc: "19" },
    { value: 20, desc: "20" },
    { value: 21, desc: "21 (Best)" },
  ].reverse();

  zoomsLevels = this.defaultZoomLevels;

  exportForm = new FormGroup({
    zoom: new FormControl(this.selectedZoom),
    clip: new FormControl(this.selectedClipMode),
  });

  @Input()
  set centerAOI(aoi: IProjectAOI) {
    if (this._centerAOI?.getLayerId() !== aoi?.getLayerId()) {
      this._centerAOI = aoi;
      if (this.mapService.getApiVersion() === "v2") {
        this.zoomsLevels = this.defaultZoomLevels;
        if (this._centerAOI) {
          this.zoomsLevels = this.zoomsLevels.filter((z) => z.value <= this._centerAOI?.getMaxZoom());
        }
        this.selectedZoom = this.zoomsLevels[0].value;
      }
      this.revalidateSelectionGeometry();
    }
    this.checkEnabledComponent();
  }

  get centerAOI(): IProjectAOI {
    return this._centerAOI;
  }

  @Input()
  set mapStatus(mapStatus: MapStatus) {
    this._mapStatus = mapStatus;
  }

  @Input()
  set swipeStatus(status: any) {
    this.swipeEnabled = status?.enable;
  }

  get mapStatus(): MapStatus {
    return this._mapStatus;
  }

  @Input() set mode(mode: any) {
    this._mode = mode;
    this.checkEnabledComponent();
  }

  get mode(): any {
    return this._mode;
  }

  @Input() set zoomEvent(event: any) {
    this._zoom = event?.zoom;
    this.checkEnabledComponent();
  }

  @Input() set enabledTool(tool: string) {
    if (this.toolbarOption !== tool) {
      this.deactivate();
    } else {
      this.activate();
    }
  }

  @Input() set measurementStatus(status: any) {
    if (status && this.displayPanel) {
      this.togglePanelDisplay();
    }
  }

  @Input() set deactivateTool(tool: boolean) {
    this.cancelTools();
  }

  get zoom(): any {
    return this._zoom;
  }

  get exportGeoTiffEnabled(): boolean {
    return this._exportGeoTiffEnabled;
  }

  set exportGeoTiffEnabled(value: boolean) {
    if (!value && this.displayPanel) {
      this.togglePanelDisplay();
    }
    this._exportGeoTiffEnabled = value;
  }

  constructor(
    protected googleAnalyticsService: GoogleAnalyticsService,
    public mapService: MapService,
    protected apiViewerService: ApiViewerService,
    protected toolbarService: ToolbarService,
    protected measurementCommunicatorService: MeasurementCommunicatorService,
    protected matDialog: MatDialog,
    protected ngZone: NgZone,
    protected cdr: ChangeDetectorRef,
    protected flagService: FeatureFlagsService,
  ) {}

  FeatureFlags = FeatureFlags;

  ngAfterViewInit() {
    if (this.mapService.getApiVersion() === "v2") {
      this.extractValues = [
        { value: "clip", label: "Exact" },
        { value: "bounding_box", label: "Bounding box" },
      ];
      this.selectedClipMode = "clip";
    } else {
      this.extractValues = [
        { value: "exact", label: "Exact" },
        { value: "crop", label: "Bounding box" },
      ];
      this.selectedClipMode = "exact";
    }

    this.subscriptions.push(
      this.viewerService.measurementAreaUnit$.subscribe((unit) => {
        this.tools.map((t) => t.tool.setAreaUnits(unit));
      }),
    );
    this.subscriptions.push(
      this.viewerService.measurementLengthUnit$.subscribe((unit) => {
        this.tools.map((t) => t.tool.setLengthUnits(unit));
      }),
    );
  }

  ngOnDestroy(): void {
    this.deactivate();
  }

  togglePanelDisplay() {
    this.mapService.geotiffStatus = !this.displayPanel;
    this.toolbarService.enableTool(!this.displayPanel ? this.toolbarOption : toolbarOptions.NONE);
  }

  changeZoom($event: MatSelectChange) {
    this.checkTilesLimit();
  }

  changeDimensionVisibility(event: any) {
    this.tools.map((t) => t.tool.changeTooltipsVisibility(this.showDimensions));
  }

  changeClipMode($event: MatSelectChange) {
    this.tools.map((t) => t.tool.updateMask(this.selectedClipMode));
  }

  downloadGeoTiff() {
    this.errorMessage = null;

    this.downloadingGeoTiff = true;

    const geometryWkt = GeoService.geometryToWKT(this.selectionGeometry, 7);

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.EXPORT_GEOTIFF_CLICK_DOWNLOAD,
      GoogleAnalyticsConstantsCategory.EXPORT_GEOTIFF,
      true,
    );

    this.mapService
      .exportGeoTiff(
        this.resolveExportLayer(),
        this.centerAOI.name,
        this.selectedZoom,
        this.selectedClipMode,
        geometryWkt,
        this.viewerService.getInfraredStatus(),
      )
      .subscribe({
        next: (response) => {
          saveAs(response, `geotiff_${this.resolveExportLayer()}_${this.selectedZoom}_${this.selectedClipMode}.tiff`);

          this.downloadingGeoTiff = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.errorMessage = "Error downloading image";
          this.downloadingGeoTiff = false;
          this.cdr.detectChanges();
        },
      });
  }

  openDialogMultiDownloadGeoTIFF() {
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.EXPORT_GEOTIFF_OPEN_MULTIPLE_EXPORTS,
      GoogleAnalyticsConstantsCategory.EXPORT_GEOTIFF,
      true,
    );
    const dialog = this.matDialog.open(PhoenixMultiExportGeotiffDialog, {
      autoFocus: false,
      panelClass: "medium-panel-dialog",
    });
    dialog.afterClosed().subscribe((result) => {
      if (result) {
        try {
          this.multiDownloadGeoTIFF(result.zipFile);
        } catch (err) {
          this.errorMessage = err;
          this.downloadingGeoTiff = false;
          this.cdr.detectChanges();
        }
      }
    });
  }

  multiDownloadGeoTIFF(zipFile: boolean) {
    this.errorMessage = null;

    this.downloadingGeoTiff = true;
    this.cdr.detectChanges();
    const geometryWkt = GeoService.geometryToWKT(this.selectionGeometry, 20);
    const geoJsonGeometry = GeoService.wktToGeoJSONPolygon(geometryWkt, false);
    const bbox = GeoService.bboxFromPoints(this.selectionGeometry.getExtent());

    const gridGeometry = GeoService.getGridOfBbox(
      bbox.bbox,
      this.viewerService.getCoreMap().getTileGrid(),
      this.selectedZoom - 5,
    );

    const requests = [];
    gridGeometry?.forEach((f) => {
      const intersection = GeoService.getIntersection(f, geoJsonGeometry);

      if (intersection) {
        const intersectionWKT = GeoService.geoJSONFeatureToWKT(intersection);
        requests.push(
          this.mapService.exportGeoTiff(
            this.resolveExportLayer(),
            this.centerAOI.name,
            this.selectedZoom,
            this.selectedClipMode,
            intersectionWKT,
            this.viewerService.getInfraredStatus(),
          ),
        );
      }
    });
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.EXPORT_GEOTIFF_CLICK_EXPORT_MULTIPLE,
      GoogleAnalyticsConstantsCategory.EXPORT_GEOTIFF,
      true,
    );

    if (zipFile) {
      forkJoin(requests).subscribe({
        next: (responses) => {
          const zip = new JSZip();

          responses.forEach((r, index) => {
            zip.file(
              `geotiff_${this.resolveExportLayer()}_${this.selectedZoom}_${this.selectedClipMode}-${index}.tiff`,
              r,
            );
          });

          return zip.generateAsync({ type: "blob" }).then(
            (blob) => {
              saveAs(blob, `geoTIFFs_${this.resolveExportLayer()}_${this.selectedZoom}_${this.selectedClipMode}.zip`);
              this.downloadingGeoTiff = false;
              this.cdr.detectChanges();
            },
            (err) => {
              this.errorMessage = err;
              this.downloadingGeoTiff = false;
              this.cdr.detectChanges();
            },
          );
        },
        error: (e) => {
          this.errorMessage = e;
          this.downloadingGeoTiff = false;
          this.cdr.detectChanges();
        },
      });
    } else {
      let requestsDone = 0;
      requests.forEach((r, index) => {
        r.subscribe({
          next: (response) =>
            saveAs(
              response,
              `geotiff_${this.resolveExportLayer()}_${this.selectedZoom}_${this.selectedClipMode}-${index}.tiff`,
            ),
          error: (e) => (this.errorMessage = e),
          complete: () => {
            requestsDone += 1;
            if (requestsDone === requests.length) {
              this.downloadingGeoTiff = false;
            }
            this.cdr.detectChanges();
          },
        });
      });
    }
  }

  selectTool(tool: any) {
    const auxSelectedTool = this.selectedTool;

    this.cancelTools();

    this.deactivateTools();

    this.deactivateEdit();

    this.multipleCollections = false;

    if (!auxSelectedTool || tool !== auxSelectedTool.id) {
      this.clearWorkspace();

      this.selectedTool = this.tools.find((t) => t.id === tool);
      this.selectedTool.tool.activate();
    }
  }

  toggleEdit() {
    if (this.checkVerticesOutsideCenterAOI(this.selectionGeometry) && this.editActionEnabled) {
      this.openMultipleCollectionsWarning();
    } else {
      this.multipleCollections = false;
      this.deactivateTools();
      if (this.editActionEnabled) {
        this.deactivateEdit();
      } else {
        this.activateEdit();
      }
    }
  }

  clearWorkspace() {
    this.errorMessage = "";
    this.warningMessage = "";
    this.tilesSelected = null;
    this.selectionGeometry = null;

    this.tools.map((t) => t.tool.clear());
  }

  getTooltip() {
    return !this.swipeEnabled ? "Export GeoTIFF" : "Export GeoTIFF is not available when Before/After tool is open";
  }

  openUploadDialog() {
    const dialog = this.matDialog.open(UploadSelectionFileDialogComponent, {
      data: {
        title: "Add selection feature from file or plain text",
        viewerService: this.viewerService,
      },
      panelClass: "medium-panel-dialog",
    });
    dialog.afterClosed().subscribe((feature) => {
      this.deactivateEdit();
      this.clearWorkspace();
      if (feature) {
        const geometry = feature.geometry;
        const wouldExceedZoomLevel = this.wouldExceedMinZoomLevel(geometry);

        if (wouldExceedZoomLevel) {
          this.errorMessage =
            "The imported geometry is too large and would cause the tool to close. Please import a smaller geometry.";
          this.cdr.detectChanges();
          return;
        }

        if (feature.properties?.isCircle || feature.properties?.measurement?.tool === Tools.MEASURE_CIRCLE) {
          const circleSelection = this.tools.find((t) => t.id === this.toolsIds.SELECTION_CIRCLE);
          circleSelection.tool.addSelectionCircle(feature);
        } else {
          const selectionPolygon = this.tools.find((t) => t.id === this.toolsIds.SELECTION_POLYGON);
          selectionPolygon.tool.addSelectionPolygon(feature);
        }

        const extent = GeoService.bbox(feature.geometry);
        this.mapService.fitToExtentWithMinZoom(
          extent,
          ViewerConstants.PROJECTIONS.WebMercator,
          ViewerConstants.COLLECTION_TIMELINE_ZOOM_LEVEL,
        );
      }
      this.cdr.detectChanges();
    });
  }

  wouldExceedMinZoomLevel(geometry: any): boolean {
    try {
      const geoJSONFormat = new GeoJSON();

      const olGeometry = geoJSONFormat.readGeometry(geometry, {
        dataProjection: ViewerConstants.PROJECTIONS.WebMercator,
        featureProjection: ViewerConstants.PROJECTIONS.WGS84,
      });

      const tilesAtMinZoom = this.viewerService.countGeometryTilesForZoom(
        olGeometry,
        ViewerConstants.COLLECTION_TIMELINE_ZOOM_LEVEL,
      );

      return tilesAtMinZoom > this.MULTI_EXPORT_TILES_LIMIT;
    } catch (error) {
      console.error("Error in wouldExceedMinZoomLevel:", error);
      return false;
    }
  }

  private activate() {
    this.displayPanel = true;

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.EXPORT_GEOTIFF_OPEN_TOOL,
      GoogleAnalyticsConstantsCategory.EXPORT_GEOTIFF,
      true,
    );

    this.tools = [
      {
        id: this.toolsIds.SELECTION_SQUARE,
        tool: new SquareSelectionTool({
          map: this.viewerService.getCoreMap(),
          showTooltip: this.showDimensions,
          areaUnits: this.viewerService.getMeasurementAreaUnit(),
          lengthUnits: this.viewerService.getMeasurementLengthUnit(),
          initCallback: this.manageInitSelectionGeometry.bind(this),
          callback: this.manageSelectionGeometry.bind(this),
        }),
      },
      {
        id: this.toolsIds.SELECTION_POLYGON,
        tool: new PolygonSelectionTool({
          map: this.viewerService.getCoreMap(),
          showTooltip: this.showDimensions,
          areaUnits: this.viewerService.getMeasurementAreaUnit(),
          lengthUnits: this.viewerService.getMeasurementLengthUnit(),
          initCallback: this.manageInitSelectionGeometry.bind(this),
          callback: this.manageSelectionGeometry.bind(this),
        }),
      },
      {
        id: this.toolsIds.SELECTION_CIRCLE,
        tool: new CircleSelectionTool({
          map: this.viewerService.getCoreMap(),
          showTooltip: this.showDimensions,
          areaUnits: this.viewerService.getMeasurementAreaUnit(),
          lengthUnits: this.viewerService.getMeasurementLengthUnit(),
          initCallback: this.manageInitSelectionGeometry.bind(this),
          callback: this.manageCircleSelectionGeometry.bind(this),
        }),
      },
    ];

    this.measurementCommunicatorService.setMeasuremenToolStatus(false);
  }

  private deactivate() {
    this.clearWorkspace();

    this.displayPanel = false;

    this.deactivateTools();

    this.selectedTool = null;
    this.tools = [];
  }

  private deactivateEdit() {
    this.editActionEnabled = false;
    this.tools.map((t) => t.tool.disableEdit());
  }

  private activateEdit() {
    this.deactivateTools();

    this.editActionEnabled = true;
    // todo this fails
    const collectionCoverage = this.mapService.getCollectionManager().getCollectionsById(this.centerAOI.getName());
    this.tools.map((t) => t.tool.enableEdit(collectionCoverage.geometry));

    // this.viewerService.enableEditSelections();
  }

  private deactivateTools() {
    this.tools?.map((t) => {
      t.tool.deactivate();
    });

    this.selectedTool = null;
  }

  private cancelTools() {
    this.tools
      .map((t) => t.tool)
      .filter((tool) => tool.active)
      .forEach((tool) => tool.cancel());
  }

  private checkTilesLimit() {
    this.warningMessage = null;

    this.tilesSelected = this.viewerService.countGeometryTilesForZoom(this.selectionGeometry, this.selectedZoom);

    this.tilesLimitExceeded = this.tilesSelected > this.TILES_LIMIT;

    this.multiExportTilesLimitExceeded = this.tilesSelected > this.MULTI_EXPORT_TILES_LIMIT;
    if (this.tilesLimitExceeded && !this.multiExportTilesLimitExceeded)
      this.warningMessage =
        "The selected area exceeds the maximum single download size (1024 tiles)." +
        " Download at lower resolution (zoom), change the selection, or";
    else if (this.tilesLimitExceeded && this.multiExportTilesLimitExceeded)
      this.warningMessage =
        "The selected area exceeds the maximum total download size (12288 tiles)." +
        " Download at lower resolution (zoom) or change the selection.";
    this.cdr.detectChanges();
  }

  private manageInitSelectionGeometry() {
    this.clearWorkspace();
    this.cdr.detectChanges();
  }

  private manageSelectionGeometry(result) {
    this.handleSelectionResult(result);
  }

  private manageCircleSelectionGeometry(result) {
    this.handleSelectionResult(result);
  }

  private handleSelectionResult(result) {
    this.ngZone.run(() => {
      if (result.geometry) {
        // Calculate intersection between selection and image bbox
        this.selectionGeometry = this.extractPolygonFromGeometryCollection(result.geometry);
        this.checkTilesLimit();
        if (this.checkVerticesOutsideCenterAOI(this.selectionGeometry)) {
          this.handleOutsideAOIWarning();
        }
      } else {
        this.errorMessage = "Error on selection geometry";
        this.cdr.detectChanges();
      }
    });
  }

  private checkEnabledComponent() {
    this.exportGeoTiffEnabled =
      this.mode?.name === ViewerConstants.COLLECTION_MODES.VERTICAL.name &&
      this.zoom >= ViewerConstants.COLLECTION_TIMELINE_ZOOM_LEVEL &&
      !!this.centerAOI;

    this.cdr.detectChanges();
  }

  private extractPolygonFromGeometryCollection(geometry) {
    if (geometry.getType() === "GeometryCollection") {
      const polygon = geometry.getGeometries().find((g) => g.getType() === "Polygon");
      if (!polygon) {
        this.errorMessage = "No polygon found in geometry collection";
        this.cdr.detectChanges();
        return null;
      }
      return polygon;
    } else {
      return geometry;
    }
  }

  private resolveExportLayer(): any {
    return this.mapService.getCoreMap().getApiVersion() === "v1"
      ? this.centerAOI.getLayer()
      : this.centerAOI.getSourceLayer();
  }

  private openMultipleCollectionsWarning() {
    this.matDialog.open(MultipleCollectionsWarningComponent, {
      height: "250px",
      width: "400px",
      id: "multiple-collections-dialog",
      autoFocus: false,
    });
  }

  private handleOutsideAOIWarning() {
    setTimeout(() => {
      if (!this.editActionEnabled) {
        this.multipleCollections = true;
        this.activateEdit();
        this.openMultipleCollectionsWarning();
      }
      this.cdr.detectChanges();
    }, 100);
  }

  private checkVerticesOutsideCenterAOI(geometry) {
    const geoJSON = GeoService.toMercator(GeoService.geometryToGeoJSON(geometry));
    const centerCollection = this.mapService.getCollectionManager().getCollectionsById(this.centerAOI.getName());
    return !this.mapService.getCollectionManager().contains(centerCollection, geoJSON);
  }

  private revalidateSelectionGeometry() {
    if (!this.selectionGeometry || !this.displayPanel || !this._centerAOI) return;

    this.multipleCollections = false;
    this.warningMessage = null;
    this.errorMessage = null;

    this.checkTilesLimit();

    if (this.checkVerticesOutsideCenterAOI(this.selectionGeometry)) {
      this.multipleCollections = true;
      if (!this.editActionEnabled) {
        this.activateEdit();
        this.openMultipleCollectionsWarning();
      }
    } else {
      if (this.editActionEnabled) {
        this.deactivateEdit();
      }
    }

    this.cdr.detectChanges();
  }
}
